import { useState, useEffect } from 'react';
// @ts-ignore
import backupService from '../../../../services/backupService';

const BackupSettings = () => {
  const [settings, setSettings] = useState<any>(null);
  const [backups, setBackups] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    frequency: 'daily',
    time: '02:00',
    retentionDays: 7,
    includes: {
      database: true,
      files: true,
      config: true,
    },
    enabled: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const [settingsRes, backupsRes, statsRes] = await Promise.all([
        backupService.getSettings(),
        backupService.getAllBackups(),
        backupService.getStatistics(),
      ]);

      if (settingsRes.success && settingsRes.data) {
        setSettings(settingsRes.data);
        setFormData({
          frequency: settingsRes.data.frequency || 'daily',
          time: settingsRes.data.time || '02:00',
          retentionDays: settingsRes.data.retentionDays || 7,
          includes: settingsRes.data.includes || {
            database: true,
            files: true,
            config: true,
          },
          enabled: settingsRes.data.enabled || false,
        });
      }

      if (backupsRes.success) {
        setBackups(backupsRes.data || []);
      }

      if (statsRes.success) {
        setStatistics(statsRes.data || null);
      }
    } catch (err: any) {
      console.error('Fetch data error:', err);
      setError(err?.message || 'Không thể tải dữ liệu sao lưu');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const response = await backupService.updateSettings(formData);

      if (response.success) {
        setSuccess('Cài đặt sao lưu đã được cập nhật thành công');
        setSettings(response.data);
      }
    } catch (err: any) {
      setError(err?.message || 'Không thể cập nhật cài đặt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      setBackupProgress(0);
      setError(null);
      setSuccess(null);

      // Simulate progress: 0% -> 30% (chuẩn bị)
      const progressInterval = setInterval(() => {
        setBackupProgress((prev) => {
          if (prev < 30) return prev + 5;
          if (prev < 60) return prev + 3;
          if (prev < 90) return prev + 1;
          return prev;
        });
      }, 200);

      const response = await backupService.createBackup(formData.includes);

      clearInterval(progressInterval);

      if (response.success) {
        // Complete progress: 90% -> 100%
        setBackupProgress(90);
        setTimeout(() => setBackupProgress(95), 100);
        setTimeout(() => setBackupProgress(100), 300);
        
        setSuccess('Sao lưu đã hoàn thành thành công!');
        setTimeout(() => {
          setBackupProgress(0);
          fetchData(false); // Don't show loading screen
        }, 1500);
      }
    } catch (err: any) {
      setError(err?.message || 'Không thể tạo sao lưu');
      setBackupProgress(0);
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = async (backupId: string) => {
    try {
      setDownloadingId(backupId);
      setError(null);
      setSuccess(null);
      await backupService.downloadBackup(backupId);
      setSuccess('Tải xuống backup thành công');
    } catch (err: any) {
      setError(err?.message || 'Không thể tải xuống backup');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteBackup = async (backupId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa backup này?')) return;

    try {
      setError(null);
      const response = await backupService.deleteBackup(backupId);
      
      if (response.success) {
        setSuccess('Backup đã được xóa');
        fetchData(false); // Don't show loading screen
      }
    } catch (err: any) {
      setError(err?.message || 'Không thể xóa backup');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#2A4B7C] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Sao lưu</h2>
        <p className="text-gray-600">Quản lý sao lưu và khôi phục dữ liệu hệ thống</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
          <i className="ri-error-warning-line text-xl mt-0.5"></i>
          <div>
            <p className="font-semibold">Có lỗi xảy ra</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-xl mt-0.5"></i>
          <div>
            <p className="font-semibold">Thành công</p>
            <p className="text-sm">{success}</p>
          </div>
        </div>
      )}

      {downloadingId && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-start gap-3">
          <div className="w-5 h-5 border-2 border-blue-700 border-t-transparent rounded-full animate-spin mt-0.5"></div>
          <div>
            <p className="font-semibold">Đang tải xuống</p>
            <p className="text-sm">Vui lòng đợi, file backup đang được tải về...</p>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <i className="ri-save-line text-2xl text-white"></i>
              </div>
              <span className="text-3xl font-bold text-blue-900">{statistics.completed}</span>
            </div>
            <h3 className="text-sm font-semibold text-blue-900">Sao lưu gần nhất</h3>
            <p className="text-xs text-blue-700 mt-1">
              {statistics.latestBackup
                ? formatDate(statistics.latestBackup.createdAt)
                : 'Chưa có'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <i className="ri-database-2-line text-2xl text-white"></i>
              </div>
              <span className="text-3xl font-bold text-green-900">{statistics.totalSizeMB}</span>
            </div>
            <h3 className="text-sm font-semibold text-green-900">Kích thước</h3>
            <p className="text-xs text-green-700 mt-1">MB</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-2xl text-white"></i>
              </div>
              <span className="text-3xl font-bold text-orange-900">{formData.retentionDays}</span>
            </div>
            <h3 className="text-sm font-semibold text-orange-900">Sao lưu tiếp theo</h3>
            <p className="text-xs text-orange-700 mt-1">
              {settings?.nextBackupAt
                ? formatDate(settings.nextBackupAt)
                : 'Chưa đặt lịch'}
            </p>
          </div>
        </div>
      )}

      {/* Backup Settings Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Cài đặt sao lưu</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tần suất sao lưu
            </label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            >
              <option value="daily">Hàng ngày</option>
              <option value="weekly">Hàng tuần</option>
              <option value="monthly">Hàng tháng</option>
            </select>
          </div>

          {/* Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Giờ sao lưu
            </label>
            <input
              type="time"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>

          {/* Retention Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Số lượng backup giữ lại
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={formData.retentionDays}
              onChange={(e) =>
                setFormData({ ...formData, retentionDays: parseInt(e.target.value) })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Backup cũ hơn {formData.retentionDays} ngày sẽ được tự động xóa
            </p>
          </div>

          {/* Includes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Nội dung sao lưu
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.includes.database}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      includes: { ...formData.includes, database: e.target.checked },
                    })
                  }
                  className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C]"
                />
                <div>
                  <span className="font-medium text-gray-900">Cơ sở dữ liệu</span>
                  <p className="text-xs text-gray-500">Tất cả dữ liệu MongoDB</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.includes.files}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      includes: { ...formData.includes, files: e.target.checked },
                    })
                  }
                  className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C]"
                />
                <div>
                  <span className="font-medium text-gray-900">Tệp tin và hình ảnh</span>
                  <p className="text-xs text-gray-500">Thư mục uploads</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.includes.config}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      includes: { ...formData.includes, config: e.target.checked },
                    })
                  }
                  className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C]"
                />
                <div>
                  <span className="font-medium text-gray-900">Cấu hình hệ thống</span>
                  <p className="text-xs text-gray-500">File .env và package.json</p>
                </div>
              </label>
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              <h4 className="font-medium text-gray-900">Tự động sao lưu</h4>
              <p className="text-sm text-gray-500">Bật/tắt sao lưu tự động theo lịch</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2A4B7C]"></div>
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-[#2A4B7C] text-white font-semibold rounded-lg hover:bg-[#1e3a5f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Đang lưu...' : 'Lưu cài đặt'}
            </button>

            <button
              type="button"
              onClick={handleCreateBackup}
              disabled={creating}
              className="flex-1 px-6 py-3 bg-[#00C897] text-white font-semibold rounded-lg hover:bg-[#00a67a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {creating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Đang sao lưu...</span>
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  <span>Sao lưu ngay</span>
                </>
              )}
            </button>
          </div>

          {/* Progress Bar */}
          {creating && backupProgress > 0 && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 font-medium">Đang sao lưu dữ liệu...</span>
                <span className="text-[#2A4B7C] font-bold">{backupProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-[#2A4B7C] to-[#00C897] h-full rounded-full transition-all duration-300 ease-out flex items-center justify-end"
                  style={{ width: `${backupProgress}%` }}
                >
                  <div className="w-1 h-full bg-white/30 animate-pulse"></div>
                </div>
              </div>
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-information-line"></i>
                {backupProgress < 30 && 'Đang chuẩn bị...'}
                {backupProgress >= 30 && backupProgress < 60 && 'Đang sao lưu cơ sở dữ liệu...'}
                {backupProgress >= 60 && backupProgress < 90 && 'Đang sao lưu tệp tin từ MinIO...'}
                {backupProgress >= 90 && 'Đang hoàn tất...'}
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Backups List */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Danh sách sao lưu</h3>
        </div>

        {backups.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <i className="ri-inbox-line text-5xl mb-4"></i>
            <p>Chưa có backup nào được tạo</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {backups.map((backup) => (
              <div key={backup._id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900">{backup.fileName}</h4>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          backup.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : backup.status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {backup.status === 'completed'
                          ? 'Hoàn thành'
                          : backup.status === 'failed'
                          ? 'Lỗi'
                          : 'Đang xử lý'}
                      </span>
                      {backup.type === 'automatic' && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          Tự động
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {formatDate(backup.createdAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-file-line"></i>
                        {formatBytes(backup.fileSize)}
                      </span>
                      {backup.createdBy && (
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i>
                          {backup.createdBy.fullName || backup.createdBy.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {backup.status === 'completed' && (
                      <button
                        onClick={() => handleDownloadBackup(backup._id)}
                        disabled={downloadingId === backup._id}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
                        title={downloadingId === backup._id ? 'Đang tải xuống...' : 'Tải xuống'}
                      >
                        {downloadingId === backup._id ? (
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <i className="ri-download-line text-xl"></i>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteBackup(backup._id)}
                      disabled={downloadingId !== null}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Xóa"
                    >
                      <i className="ri-delete-bin-line text-xl"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BackupSettings;
